import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useMessages } from '../context/MessagesContext';
import UnreadBadge from './UnreadBadge';
import { Icon } from '../tokens';
import { SUPPORTED_LNGS, LNG_LABELS } from '../i18n';
import CurrencySelector from './CurrencySelector';
import Price from './Price';
import TrolyLockup from './TrolyLockup';
import './Navbar.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Navbar() {
  const { user, logout, updateMe } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { count: cartCount } = useCart();
  const { unreadCount } = useMessages();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const [q, setQ] = useState('');
  const [results, setResults] = useState({ listings: [], users: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setOpen(false);
        setSearchExpanded(false);
      }
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const openSearch = () => {
    setSearchExpanded(true);
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeSearch = () => {
    setSearchExpanded(false);
    setOpen(false);
    setQ('');
  };

  const currentLng = (i18n.language || 'es').slice(0, 2);
  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    // Persistir la preferencia para enviar correos (ej. digest de seguidos)
    // en el idioma elegido. Best-effort: no bloquea ni interrumpe la UI.
    if (user && user.language !== lng) {
      updateMe({ language: lng }).catch(() => {});
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) {
      setResults({ listings: [], users: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/search`, { params: { q: term, limit: 6 } });
        setResults({ listings: data.listings || [], users: data.users || [] });
      } catch {
        setResults({ listings: [], users: [] });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setOpen(false);
    navigate(`/?q=${encodeURIComponent(term)}`);
  };

  const goListing = (id) => {
    setOpen(false);
    setQ('');
    navigate(`/listing/${id}`);
  };

  const goUser = (id) => {
    setOpen(false);
    setQ('');
    navigate(`/u/${id}`);
  };

  const hasResults = results.listings.length > 0 || results.users.length > 0;

  return (
    <nav className="navbar">
      <div className="navbar__inner container">
        <Link to="/" className="navbar__logo" aria-label="Troly">
          <TrolyLockup size="22px" inverse={theme === 'dark'} />
        </Link>

        <form
          className={`navbar__search${searchExpanded ? ' navbar__search--expanded' : ''}`}
          ref={searchRef}
          onSubmit={submit}
          onFocus={() => setOpen(true)}
        >
          {Icon.search(14)}
          <input
            ref={inputRef}
            type="text"
            placeholder={t('navbar.search_placeholder', { defaultValue: 'Buscar piezas, marcas, coleccionistas...' })}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
          />
          {searchExpanded && (
            <button
              type="button"
              className="navbar__search-close"
              onClick={closeSearch}
              aria-label={t('navbar.close_search', { defaultValue: 'Cerrar búsqueda' })}
            >
              <svg width={14} height={14} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          )}
          {open && q.trim().length >= 2 && (
            <div className="search-dropdown" onMouseDown={(e) => e.preventDefault()}>
              {loading && <div className="search-empty">{t('navbar.searching', { defaultValue: 'Buscando…' })}</div>}
              {!loading && !hasResults && <div className="search-empty">{t('navbar.no_results', { defaultValue: 'Sin resultados para' })} "{q}"</div>}
              {!loading && results.listings.length > 0 && (
                <>
                  <div className="search-section">{t('navbar.pieces', { defaultValue: 'Piezas' })}</div>
                  {results.listings.map((l) => (
                    <button
                      type="button"
                      key={l._id}
                      className="search-row"
                      onClick={() => goListing(l._id)}
                    >
                      <div className="search-row__thumb">
                        {l.images?.[0]
                          ? <img src={l.images[0]} alt={l.name} />
                          : <div className="search-row__thumb-ph" />}
                      </div>
                      <div className="search-row__body">
                        <div className="search-row__title">{l.name}</div>
                        <div className="search-row__meta">
                          {l.brand}{l.category ? ` · ${l.category}` : ''}{l.scale ? ` · ${l.scale}` : ''}
                        </div>
                      </div>
                      {!l.locked && (l.price?.amount != null || typeof l.price === 'number') && (
                        <div className="search-row__price">
                          {(() => {
                            const amt = typeof l.price === 'object' ? Number(l.price.amount) : Number(l.price);
                            const cur = typeof l.price === 'object' ? (l.price.currency || 'USD') : 'USD';
                            return Number.isFinite(amt) && amt > 0
                              ? <Price amount={amt} currency={cur} />
                              : null;
                          })()}
                        </div>
                      )}
                    </button>
                  ))}
                </>
              )}
              {!loading && results.users.length > 0 && (
                <>
                  <div className="search-section">{t('navbar.collectors', { defaultValue: 'Coleccionistas' })}</div>
                  {results.users.map((u) => (
                    <button
                      type="button"
                      key={u._id}
                      className="search-row"
                      onClick={() => goUser(u._id)}
                    >
                      <div className="search-row__thumb search-row__thumb--avatar">
                        <img
                          src={u.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name)}&background=45E6C2&color=0A0A0A&size=64`}
                          alt={u.name}
                        />
                      </div>
                      <div className="search-row__body">
                        <div className="search-row__title">{u.name}</div>
                        <div className="search-row__meta">
                          {u.username ? `@${u.username}` : ''}{u.location ? ` · ${u.location}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
              {!loading && hasResults && (
                <button type="submit" className="search-all">
                  {t('navbar.see_all_results', { defaultValue: 'Ver todos los resultados' })} →
                </button>
              )}
            </div>
          )}
        </form>

        <div className="navbar__spacer" />

        <div className="navbar__actions">
          <button
            type="button"
            className="icon-btn navbar__search-toggle"
            onClick={openSearch}
            title={t('navbar.search', { defaultValue: 'Buscar' })}
            aria-label={t('navbar.search', { defaultValue: 'Buscar' })}
          >
            {Icon.search(16)}
          </button>
          {user && (
            <>
              <Link to="/" className="icon-btn" title={t('navbar.home', { defaultValue: 'Inicio' })}>{Icon.home(16)}</Link>
              <Link to="/upload" className="icon-btn" title={t('nav.upload', { defaultValue: 'Subir pieza' })}>{Icon.plus(16)}</Link>
              <Link to="/mensajes" className="icon-btn" title={t('messages.title', { defaultValue: 'Mensajes' })} style={{ position: 'relative' }}>
                {Icon.message(16)}
                <UnreadBadge count={unreadCount} />
              </Link>
              <Link to="/cart" className="icon-btn" title={t('navbar.cart', { defaultValue: 'Carrito' })} style={{ position: 'relative' }}>
                <svg width={16} height={16} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 4h2l1.5 9.5a2 2 0 0 0 2 1.7h6.5a2 2 0 0 0 2-1.6L18.5 7H6.5"/>
                  <circle cx="9" cy="17.5" r="1"/><circle cx="16" cy="17.5" r="1"/>
                </svg>
                {cartCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px',
                    borderRadius: 8, background: 'var(--teal)', color: '#0A0A0A',
                    fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>{cartCount}</span>
                )}
              </Link>
              {user.role === 'seller' && (
                <Link to="/seller" className="icon-btn" title={t('profile.seller_panel')}>
                  <svg width={16} height={16} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 4h14M5 4v12h10V4M8 8h4M8 11h4"/>
                  </svg>
                </Link>
              )}
            </>
          )}
          <button
            type="button"
            className="icon-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? t('navbar.light_mode', { defaultValue: 'Modo claro' }) : t('navbar.dark_mode', { defaultValue: 'Modo oscuro' })}
          >
            {theme === 'dark' ? Icon.sun(16) : Icon.moon(16)}
          </button>
          {user?.role === 'admin' && (
            <Link to="/admin" className="icon-btn" title={t('navbar.admin', { defaultValue: 'Admin' })}>{Icon.userPlus(16)}</Link>
          )}
          <CurrencySelector />
          <div className="lang-switch" ref={langRef}>
            <button
              type="button"
              className="icon-btn"
              title={t('nav.language', { defaultValue: 'Idioma' })}
              onClick={() => setLangOpen(v => !v)}
            >
              {Icon.globe(16)}
            </button>
            {langOpen && (
              <div className="lang-menu">
                {SUPPORTED_LNGS.map(code => (
                  <button
                    key={code}
                    type="button"
                    className={`lang-menu__item${currentLng === code ? ' lang-menu__item--active' : ''}`}
                    onClick={() => changeLang(code)}
                  >
                    <span className="lang-menu__code">{code.toUpperCase()}</span>
                    <span className="lang-menu__label">{LNG_LABELS[code]}</span>
                    {currentLng === code && <span className="lang-menu__dot" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="user-chip" onClick={() => setMenuOpen(v => !v)}>
              <img
                src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=45E6C2&color=0A0A0A&size=64`}
                alt={user.name}
              />
              {menuOpen && (
                <div className="user-menu">
                  <Link to="/profile"  onClick={() => setMenuOpen(false)}>{t('navbar.my_profile', { defaultValue: 'Mi Perfil' })}</Link>
                  <Link to="/upload"      onClick={() => setMenuOpen(false)}>{t('nav.upload', { defaultValue: 'Subir Pieza' })}</Link>
                  <Link to="/upload/bulk" onClick={() => setMenuOpen(false)}>{t('nav.bulk_upload', { defaultValue: 'Subir varias piezas' })}</Link>
                  <Link to="/cart"        onClick={() => setMenuOpen(false)}>{t('navbar.cart', { defaultValue: 'Carrito' })}{cartCount > 0 ? ` (${cartCount})` : ''}</Link>
                  {user.role === 'seller' && (
                    <Link to="/seller" onClick={() => setMenuOpen(false)}>{t('profile.seller_panel')}</Link>
                  )}
                  <Link to="/settings" onClick={() => setMenuOpen(false)}>{t('navbar.settings', { defaultValue: 'Ajustes' })}</Link>
                  <button onClick={() => { logout(); setMenuOpen(false); navigate('/'); }}>{t('navbar.logout', { defaultValue: 'Cerrar sesión' })}</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/trigger" className="btn-teal" style={{ padding: '8px 16px', fontSize: 13 }}>
              {t('nav.login', { defaultValue: 'Iniciar Sesión' })}
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
